import { Alert, AlertIcon, Box, Flex, SimpleGrid, SimpleGridProps } from "@chakra-ui/react";
import { Link, useParams } from "react-router-dom";
import { ParkOverview } from "../components/ParkOverview";
import { useGetFilteredData } from "../hooks/useGetFilteredData";
import { NumberOfParksText } from "../components/NumberOfParksText";
import { MAX_CONTENT_WIDTH, SITE_MIN_MARGIN } from "../const";
import { Helmet } from "react-helmet";
import { capitalize } from "../utils";

export const CountyInformation: React.FC = () => {
  const { county } = useParams();

  const { filteredCountyData, noMatchesCountyData } = useGetFilteredData();

  const noParksFound = filteredCountyData.length + noMatchesCountyData.length === 0;

  return (
    <>
      <Helmet>
        <title>Holiday Homes for Sale in {capitalize(county!)}</title>
        <meta name="description" content={`Browse Holiday Homes for Sale in ${county} - Caravans, Lodges and more`} />
      </Helmet>

      <NumberOfParksText />

      <Flex
        alignItems="center"
        flexDir="column"
        w="100%"
        gap={2}
        py={4}
        px={SITE_MIN_MARGIN}
        maxW={MAX_CONTENT_WIDTH}
        mx="auto"
      >
        {filteredCountyData.length > 0 && (
          <ParksGrid>
            {filteredCountyData.map((parkLocation, i) => (
              <ParkOverview key={i} parkLocationData={parkLocation} doesAnyParkMeetRequirements />
            ))}
          </ParksGrid>
        )}

        {noMatchesCountyData.length > 0 && (
          <>
            <NonMatchingParksAlert areSomeMatches={filteredCountyData.length > 0} />
            <ParksGrid>
              {noMatchesCountyData.map((parkLocation, i) => (
                <ParkOverview
                  key={`no-matches-${i}`}
                  parkLocationData={parkLocation}
                  doesAnyParkMeetRequirements={false}
                />
              ))}
            </ParksGrid>
          </>
        )}

        {noParksFound && (
          <Alert status="warning">
            <Flex flexDir="row" alignItems="center">
              <AlertIcon />
              <Flex flexDir="column">
                Sorry, we have not found any Parks in this area
                <Box maxWidth="sm">
                  <Link to=".." relative="path" style={{ textDecoration: "underline" }}>
                    Back to Areas
                  </Link>
                </Box>
              </Flex>
            </Flex>
          </Alert>
        )}
      </Flex>
    </>
  );
};

const NonMatchingParksAlert: React.FC<{ areSomeMatches: boolean }> = ({ areSomeMatches }) => (
  <Alert
    status={areSomeMatches ? "info" : "warning"}
    fontWeight="bold"
    fontSize={{ base: "18px", md: "22px" }}
    justifyContent="center"
    mt={areSomeMatches ? 8 : 0}
    mb={8}
  >
    <AlertIcon />
    {areSomeMatches
      ? "Other parks and caravans outside of your requirements available"
      : "No holiday homes match these requirements, but check out the other options below."}
  </Alert>
);

const ParksGrid: React.FC<SimpleGridProps> = (props) => (
  <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={8} w="100%" placeItems="center" {...props} />
);
